import { hoverTypes, layoutNodeTypes } from '../constants/dndAreaHoverPositionConstants';
import { DRAG_TYPES, dropTargetOrientation, dropTypes } from '../constants/dnd';
import { TOP, BOTTOM, LEFT, RIGHT } from '../constants';
import DragPreviewManager from '../DragPreview/DragPreviewManager';
import { getDropTargetIndexWithinPegboardBounds, isExistingItemDragItem, getDragPreviewComputedPegboardSpanning } from './dropTargetUtils';
import { getHoveredIndex } from '../hoverPositions/gridLayoutTreeHoverPositionUtils';
function findGridCellHoverPosition(hoverPositions) {
  for (const hoverPosition of hoverPositions) {
    if (hoverPosition.isGridCell) {
      return hoverPosition;
    }
  }
  return null;
}
function calculateWrapAndInsertDropTarget(firstHoverPosition) {
  if (firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_LEFT_WHITESPACE) {
    return {
      type: dropTypes.WRAP_AND_INSERT_LEFT_OF_CONTAINER,
      containerId: firstHoverPosition.id,
      parentId: firstHoverPosition.parentId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_RIGHT_WHITESPACE) {
    return {
      type: dropTypes.WRAP_AND_INSERT_RIGHT_OF_CONTAINER,
      containerId: firstHoverPosition.id,
      parentId: firstHoverPosition.parentId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_TOP_WHITESPACE) {
    return {
      type: dropTypes.WRAP_AND_INSERT_ABOVE_CONTAINER,
      containerId: firstHoverPosition.id,
      parentId: firstHoverPosition.parentId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  } else if (firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_BOTTOM_WHITESPACE) {
    return {
      type: dropTypes.WRAP_AND_INSERT_BELOW_CONTAINER,
      containerId: firstHoverPosition.id,
      parentId: firstHoverPosition.parentId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
}
function isHoveringNearLastGridElement(hoverPositions, dragItem) {
  if (hoverPositions.length === 0 || !(dragItem.type === DRAG_TYPES.ADD_BUILDING_BLOCK || dragItem.type === DRAG_TYPES.ADD_MODULE)) {
    return false;
  }
  const [firstHoverPosition] = hoverPositions;
  if (!firstHoverPosition.isGridCell) {
    return false;
  }
  const gridTableHoverPosition = hoverPositions.find(hp => hp.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID);
  const isLastChildOfGridTable = gridTableHoverPosition && gridTableHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID && gridTableHoverPosition.lastChildId === firstHoverPosition.id;
  if (!isLastChildOfGridTable) {
    return false;
  }
  return Boolean(firstHoverPosition.isCloseToLeftOrRightEdge && firstHoverPosition.leftOrRightHovered === RIGHT);
}
function findNearestHoveredSection(hoverPositions) {
  return hoverPositions.find(hoverPos => {
    return hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_SECTION;
  });
}
function calculateSectionDropTarget(hoverPositions) {
  // TODO: Handle empty row/column cases
  // Else hovering over an existing section
  const sectionHoveredOver = findNearestHoveredSection(hoverPositions);
  // If we are hovering over the top / bottom margin area of a top level

  if (sectionHoveredOver) {
    const dropTargetType = sectionHoveredOver.topOrBottomHovered === TOP ? dropTypes.MOVE_SECTION_BEFORE : dropTypes.MOVE_SECTION_AFTER;
    return {
      type: dropTargetType,
      targetedSectionId: sectionHoveredOver.id,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
}

/**
 * In cases where you have a stack nested within a stack, or are hovering the
 * top/bottom most edge of a module that's snug with the edge of the parent stack
 * we assume a user wants to drag into a potential parent stack. So we walk the
 * hover stack to find the nearest vertical stack.
 *
 * @param hoverPositions The current hover stack
 * @param triangleHovered Used to determine if we should insert above/below the element
 * @param parentOrStackId The ID of the current hovered stack or module's parent stack we should ignore
 *
 * @returns A drop target if there's a valid vertical stack to target, or null if there isn't
 */
function calculateNearestVerticalStackDropTarget({
  hoverPositions,
  triangleHovered,
  parentOrStackId
}) {
  let nearestVerticalStackPos;
  let nearestVerticalStackChildPos;
  for (let i = 0; i < hoverPositions.length; i++) {
    const hoverPos = hoverPositions[i];
    const {
      id,
      isCloseToTopOrBottomEdge
    } = hoverPos;
    const isVerticalStack = hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK;
    if ((!isVerticalStack || id === parentOrStackId) && !isCloseToTopOrBottomEdge) {
      break;
    } else if (isVerticalStack && id !== parentOrStackId) {
      nearestVerticalStackPos = hoverPos;
      nearestVerticalStackChildPos = hoverPositions[i - 1];
    }
  }
  if (nearestVerticalStackPos && nearestVerticalStackChildPos) {
    if (triangleHovered === TOP) {
      return {
        type: dropTypes.INSERT_ELEMENT_BEFORE,
        containerId: nearestVerticalStackPos.id,
        targetedElementId: nearestVerticalStackChildPos.id,
        previousSiblingId: nearestVerticalStackChildPos.previousSiblingId,
        orientation: dropTargetOrientation.HORIZONTAL
      };
    } else if (triangleHovered === BOTTOM) {
      return {
        type: dropTypes.INSERT_ELEMENT_AFTER,
        containerId: nearestVerticalStackPos.id,
        targetedElementId: nearestVerticalStackChildPos.id,
        nextSiblingId: nearestVerticalStackChildPos.nextSiblingId,
        orientation: dropTargetOrientation.HORIZONTAL
      };
    }
  }
  return null;
}

/**
 * In cases where you have a stack nested within a stack, or are hovering the
 * left/right most edge of a module that's snug with the edge of the parent stack
 * we assume a user wants to drag into a potential parent stack. So we walk the
 * hover stack to find the nearest horizontal stack.
 *
 * @param hoverPositions The current hover stack
 * @param triangleHovered Used to determine if we should insert to the left/right of the element
 * @param parentOrStackId The ID of the current hovered stack or module's parent stack we should ignore
 *
 * @returns A drop target if there's a valid horizontal stack to target, or null if there isn't
 */
function calculateNearestHorizontalStackDropTarget({
  hoverPositions,
  triangleHovered,
  parentOrStackId
}) {
  let nearestHorizontalStackPos;
  let nearestHorizontalStackChildPos;
  for (let i = 0; i < hoverPositions.length; i++) {
    const hoverPos = hoverPositions[i];
    const {
      id,
      isCloseToLeftOrRightEdge
    } = hoverPos;
    const isHorizontalStack = hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK;
    if ((!isHorizontalStack || id === parentOrStackId) && !isCloseToLeftOrRightEdge) {
      break;
    } else if (isHorizontalStack && id !== parentOrStackId) {
      nearestHorizontalStackPos = hoverPos;
      nearestHorizontalStackChildPos = hoverPositions[i - 1];
    }
  }
  if (nearestHorizontalStackPos && nearestHorizontalStackChildPos) {
    if (triangleHovered === LEFT) {
      return {
        type: dropTypes.INSERT_ELEMENT_BEFORE,
        containerId: nearestHorizontalStackPos.id,
        targetedElementId: nearestHorizontalStackChildPos.id,
        previousSiblingId: nearestHorizontalStackChildPos.previousSiblingId,
        orientation: dropTargetOrientation.VERTICAL
      };
    } else if (triangleHovered === RIGHT) {
      return {
        type: dropTypes.INSERT_ELEMENT_AFTER,
        containerId: nearestHorizontalStackPos.id,
        targetedElementId: nearestHorizontalStackChildPos.id,
        nextSiblingId: nearestHorizontalStackChildPos.nextSiblingId,
        orientation: dropTargetOrientation.VERTICAL
      };
    }
  }
  return null;
}
function calculateDropTargetOnGridModule(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  const parentHoverPosition = hoverPositions[1];
  const {
    id,
    parentId,
    isCloseToTopOrBottomEdge,
    isCloseToLeftOrRightEdge,
    triangleHovered
  } = firstHoverPosition;
  if (isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered,
      parentOrStackId: parentId
    });
    if (dropTarget) return dropTarget;
  }
  if (isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered,
      parentOrStackId: parentId
    });
    if (dropTarget) return dropTarget;
  }
  if (parentHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK) {
    const {
      leftOrRightHovered
    } = firstHoverPosition;
    if (leftOrRightHovered === LEFT) {
      return {
        type: dropTypes.INSERT_ELEMENT_BEFORE,
        containerId: parentId,
        targetedElementId: id,
        previousSiblingId: firstHoverPosition.previousSiblingId,
        orientation: dropTargetOrientation.VERTICAL
      };
    }
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: parentId,
      targetedElementId: id,
      nextSiblingId: firstHoverPosition.nextSiblingId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (parentHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK) {
    const {
      topOrBottomHovered
    } = firstHoverPosition;
    if (topOrBottomHovered === TOP) {
      return {
        type: dropTypes.INSERT_ELEMENT_BEFORE,
        containerId: parentId,
        targetedElementId: id,
        previousSiblingId: firstHoverPosition.previousSiblingId,
        orientation: dropTargetOrientation.HORIZONTAL
      };
    }
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: parentId,
      targetedElementId: id,
      nextSiblingId: firstHoverPosition.nextSiblingId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
  // TODO: Handle tables
}
function calculateDropTargetOnVerticalStack(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.type === hoverTypes.BEFORE_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_BEFORE,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.firstChildId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  } else if (firstHoverPosition.type === hoverTypes.AFTER_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.lastChildId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_VERTICAL_STACK_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.topId,
      nextSiblingId: firstHoverPosition.bottomId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
}
function calculateDropTargetOnHorizontalStack(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.type === hoverTypes.BEFORE_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_BEFORE,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.firstChildId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.AFTER_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.lastChildId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_HORIZONTAL_STACK_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.leftId,
      nextSiblingId: firstHoverPosition.rightId,
      orientation: dropTargetOrientation.VERTICAL
    };
  }
  return null;
}
const isNonPegboardHoverStack = hoverPositions => {
  return !hoverPositions.some(hoverPos => hoverPos.layoutNodeType === layoutNodeTypes.PEGBOARD);
};
export function calculateDropTargetFromHoverPositions(hoverPositions, dragItem) {
  if (hoverPositions.length === 0) {
    return {
      newDropTarget: null,
      isDraggingOverSelf: false
    };
  }
  const pegboardHoverPosition = hoverPositions.find(hoverPos => hoverPos.layoutNodeType === layoutNodeTypes.PEGBOARD);

  // Don't allow dropping _inside_ the thing actually being dragged (whether a single module or row/column)
  if (dragItem.dragId) {
    const hoverPositionsMinusRoot = hoverPositions.slice(0, -1);
    for (const hoverPosition of hoverPositionsMinusRoot) {
      // If we're editing a pegboard, we want to generate a hover position
      // even if we're hovered over the dragged element.
      if (hoverPosition.id === dragItem.dragId && !pegboardHoverPosition) {
        return {
          newDropTarget: null,
          isDraggingOverSelf: true
        };
      }
    }
  }
  if (!isNonPegboardHoverStack(hoverPositions)) {
    if (!pegboardHoverPosition) {
      return {
        newDropTarget: null,
        isDraggingOverSelf: true
      };
    }
    if (isExistingItemDragItem(dragItem) && dragItem.layoutNodeType === 'GRID_LAYOUT_MODULE') {
      const {
        id,
        rows,
        columns,
        rowSize,
        columnSize,
        rowGap,
        columnGap,
        measuredLeft,
        measuredTop
      } = pegboardHoverPosition;
      const dragPreviewPosition = DragPreviewManager.getInstance().getDragPreviewPosition();
      const hoveredColumn = getHoveredIndex({
        mousePosition: dragPreviewPosition.elementLeft,
        gridOffset: measuredLeft,
        trackCount: columns,
        cellSize: columnSize,
        gap: columnGap
      });
      const hoveredRow = getHoveredIndex({
        mousePosition: dragPreviewPosition.elementTop,
        gridOffset: measuredTop,
        trackCount: rows,
        cellSize: rowSize,
        gap: rowGap
      });
      const dragItemColumnsSpanned = dragItem.columnsSpanned || getDragPreviewComputedPegboardSpanning({
        elementSize: dragItem.width,
        pegboardCellSize: columnSize,
        maxPegboardCells: columns
      });
      const dragItemRowsSpanned = dragItem.rowsSpanned || getDragPreviewComputedPegboardSpanning({
        elementSize: dragItem.height,
        pegboardCellSize: rowSize,
        maxPegboardCells: rows
      });
      return {
        newDropTarget: {
          type: dropTypes.ON_PEGBOARD,
          containerId: id,
          row: getDropTargetIndexWithinPegboardBounds(hoveredRow, dragItemRowsSpanned, rows),
          column: getDropTargetIndexWithinPegboardBounds(hoveredColumn, dragItemColumnsSpanned, columns),
          dragElementId: dragItem.dragId,
          orientation: dropTargetOrientation.INSIDE
        },
        isDraggingOverSelf: false
      };
    }
    return {
      newDropTarget: {
        type: dropTypes.ON_PEGBOARD,
        containerId: pegboardHoverPosition.id,
        column: pegboardHoverPosition.hoveredColumn,
        row: pegboardHoverPosition.hoveredRow,
        orientation: dropTargetOrientation.INSIDE,
        dragElementId: dragItem.dragId
      },
      isDraggingOverSelf: false
    };
  }
  const firstHoverPosition = hoverPositions[0];
  const isDraggingSection = dragItem.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_SECTION;
  const gridCellHoverPosition = findGridCellHoverPosition(hoverPositions);
  if (firstHoverPosition.type === hoverTypes.BETWEEN_GRID_TABLE_CHILDREN) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_AFTER_TABLE_CELL,
        containerId: firstHoverPosition.id,
        targetedCellId: firstHoverPosition.leftId,
        nextSiblingId: firstHoverPosition.rightId,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  }
  if (firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_LEFT_WHITESPACE || firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_RIGHT_WHITESPACE || firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_TOP_WHITESPACE || firstHoverPosition.type === hoverTypes.SECTION_CONTAINER_BOTTOM_WHITESPACE) {
    return {
      newDropTarget: calculateWrapAndInsertDropTarget(firstHoverPosition),
      isDraggingOverSelf: false
    };
  }
  if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID && (dragItem.type === DRAG_TYPES.ADD_BUILDING_BLOCK || dragItem.type === DRAG_TYPES.ADD_MODULE)) {
    return {
      newDropTarget: {
        type: dropTypes.APPEND_GRID_CELL,
        containerId: firstHoverPosition.id,
        lastChildId: firstHoverPosition.lastChildId,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  }
  if (isHoveringNearLastGridElement(hoverPositions, dragItem)) {
    const gridTableHoverPosition = hoverPositions.find(hp => hp.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID);
    if (gridTableHoverPosition && gridTableHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID) {
      return {
        newDropTarget: {
          type: dropTypes.APPEND_GRID_CELL,
          containerId: gridTableHoverPosition.id,
          lastChildId: gridTableHoverPosition.lastChildId,
          orientation: dropTargetOrientation.VERTICAL
        },
        isDraggingOverSelf: false
      };
    }
  }
  if (gridCellHoverPosition && dragItem.layoutNodeType && dragItem.isGridCell && (gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_MODULE || gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK || gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK)) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_TABLE_CELL,
        containerId: gridCellHoverPosition.parentId,
        targetedCellId: gridCellHoverPosition.id,
        orientation: dropTargetOrientation.INSIDE
      },
      isDraggingOverSelf: false
    };
  }
  if (firstHoverPosition.isEmptyGridLayout && isDraggingSection) {
    return {
      newDropTarget: null,
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isEmptyGridLayout) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_GRID_LAYOUT,
        gridLayoutId: firstHoverPosition.gridLayoutId,
        orientation: dropTargetOrientation.INSIDE
      },
      isDraggingOverSelf: false
    };
  } else if (isDraggingSection) {
    return {
      newDropTarget: calculateSectionDropTarget(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && firstHoverPosition.numChildren === 0) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_STACK,
        containerId: firstHoverPosition.id,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && firstHoverPosition.numChildren === 0) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_STACK,
        containerId: firstHoverPosition.id,
        orientation: dropTargetOrientation.HORIZONTAL
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_MODULE && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE) {
    return {
      newDropTarget: calculateDropTargetOnGridModule(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK) {
    return {
      newDropTarget: calculateDropTargetOnVerticalStack(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK) {
    return {
      newDropTarget: calculateDropTargetOnHorizontalStack(hoverPositions),
      isDraggingOverSelf: false
    };
  }

  // No drop target to render
  return {
    newDropTarget: null,
    isDraggingOverSelf: false
  };
}